import { useApolloClient, useQuery } from '@apollo/react-hooks';
import BasicFieldType from '@opsbase/lib-data-fieldtype/BasicFieldType/BasicFieldType';
import findFilterByKey from '@opsbase/lib-data-query/helpers/findFilterByKey';
import QueryFieldKey from '@opsbase/lib-data-queryfield/QueryFieldKey';
import QueryFieldRegistry from '@opsbase/lib-data-queryfield/QueryFieldRegistry/QueryFieldRegistry';
import DataType from '@opsbase/lib-datatype/DataType';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette/Palette/Palette';
import { DefaultTimezone } from '@opsbase/lib-time/consts';
import usePlaceDelete from '@opsbase/react-gql-hooks/place/usePlaceDelete';
import usePlaceUpsert from '@opsbase/react-gql-hooks/place/usePlaceUpsert';
import { rootLevelPlacesQuery } from '@opsbase/react-gql-hooks/place/useRootLevelPlaces';
import useForceUpdate from '@opsbase/react-hooks/useForceUpdate';
import markdown from '@opsbase/react-markdown';
import AppPage from '@opsbase/web-app/AppPage/AppPage';
import useCss from '@opsbase/web-css';
import TimezoneChooser from '@opsbase/web-data-choosers/TimezoneChooser/TimezoneChooser';
import ValueChooser from '@opsbase/web-data-choosers/ValueChooser';
import ConfirmDialog from '@opsbase/web-dialogs/ConfirmDialog';
import RepeatIcon from '@opsbase/web-icons/icons/RepeatRoundedIcon';
import Horizontal from '@opsbase/web-layout/Horizontal/Horizontal';
import Pane from '@opsbase/web-layout/Pane/Pane';
import Vertical from '@opsbase/web-layout/Vertical/Vertical';
import TextField from '@opsbase/web-textfield/TextField';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import ActionButton from '@opsbase/web-ui/ActionButton';
import ActionButtonContainer from '@opsbase/web-ui/ActionButtonContainer';
import Button from '@opsbase/web-ui/Button';
import Dots from '@opsbase/web-ui/Dots';
import { SidePaneStack } from '@opsbase/web-ui/SidePane/SidePaneStack';
import Spinner from '@opsbase/web-ui/Spinner/Spinner';
import Switch from '@opsbase/web-ui/Switch';
import Text from '@opsbase/web-ui/Text';
import gql from 'graphql-tag';
import uniq from 'lodash/uniq';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as uuid from 'uuid';
import PlaceDisplayItem from './PlaceDisplayItem';
import PlaceFiltersBar from './PlaceFiltersBar';
import PlaceList from './PlaceList';
const makeMyClasses = () => ({
    editBox: {
        border: `1px solid ${Palette.LineGrey.main}`,
        borderRadius: 8,
        width: 250,
    },
    placeList: {
        '& .EditableValue': {
            minWidth: 180,
        },
        '& .NewItem .EditableValue': {
            border: '0 !important',
            background: 'transparent !important',
        },
        '& .selected .EditableValue': {
            backgroundColor: `${Palette.LightBlue.main} !important`,
        },
        '& .showing .EditableValue': {
            border: `1px solid ${Palette.secondary.main} !important`,
            boxShadow: `0 0 20px -8px ${Palette.BlackCoral.main}`,
        },
        width: 300,
    },
});
const placeCountQuery = gql `
	query GetPlaceCount {
		places {
			totalCount
		}
	}
`;
const defaultPlaceLevelInfo = {
    places: [],
    allPlaceIds: [],
    loading: false,
    loaded: 0,
    max: 0,
    shown: 0,
    filtered: 0,
    parentPlaceId: null,
};
export function SetupPlacesPage() {
    var _a, _b, _c, _d;
    const filters = useRef([]);
    const { loading: loadingPlaceCount, data: placeCountData, refetch: refetchPlaceCount, } = useQuery(placeCountQuery);
    const upsertPlace = usePlaceUpsert();
    const deletePlace = usePlaceDelete();
    const [newTags, setNewTags] = useState([]);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [checkTitle, setCheckTitle] = useState();
    const placeLookup = useRef({});
    const selectedPlaces = useRef([]);
    const placeToShowDetails = useRef();
    const placeStack = useRef([]);
    const totalCount = useRef();
    const placeToDelete = useRef();
    const isDirty = useRef(false);
    const myCss = useCss(makeMyClasses);
    const { forceUpdate, tick, acknowledgeUpdate } = useForceUpdate();
    const updatePlaceLookup = useCallback((places) => {
        for (const p of places) {
            placeLookup.current[p.id] = p;
        }
    }, []);
    const updatePlaceStack = useCallback((places, max, parentPlaceId, level = 0, offset = 0) => {
        var _a, _b;
        const parent = level === 0 ? null : selectedPlaces.current[level - 1];
        if (parent && parent.id !== parentPlaceId)
            return;
        const aPageOfPlaces = places.map(z => {
            const parts = z.title.split('>');
            const placeTitle = parts[parts.length - 1].trim();
            z.title = placeTitle;
            return z;
        });
        updatePlaceLookup(aPageOfPlaces);
        const keyword = (_a = filters.current.find(f => f.queryFieldKey === QueryFieldKey.KEYWORD)) === null || _a === void 0 ? void 0 : _a.id;
        const tag = filters.current.filter(f => f.queryFieldKey === QueryFieldKey.PLACE_TAG);
        const enabled = filters.current.filter(f => f.queryFieldKey === QueryFieldKey.PLACE_ENABLED);
        let newPlaces = aPageOfPlaces;
        if (offset > 0 && placeStack.current[level]) {
            newPlaces = placeStack.current[level].allPlaceIds.map(s => placeLookup.current[s]);
            aPageOfPlaces.forEach((z, i) => newPlaces.splice(offset + i, 1, z));
        }
        const allPlaceIds = newPlaces.map(z => z.id);
        const filteredPlaces = newPlaces
            .filter(z => {
            return ((!(keyword === null || keyword === void 0 ? void 0 : keyword.length) || z.matchesKeywordQuery) &&
                (!(tag === null || tag === void 0 ? void 0 : tag.length) || z.matchesTagQuery) &&
                (!(enabled === null || enabled === void 0 ? void 0 : enabled.length) ||
                    (z.enabled && enabled[0].id === 'TRUE') ||
                    (!z.enabled && enabled[0].id === 'FALSE')));
        })
            .map(z => {
            z.shouldBeDimmed =
                ((keyword === null || keyword === void 0 ? void 0 : keyword.length) &&
                    z.title.toLocaleLowerCase().indexOf(keyword.toLocaleLowerCase()) ===
                        -1) ||
                    ((tag === null || tag === void 0 ? void 0 : tag.length) && tag.some(t => !z.tags.includes(t.id)));
            return z;
        });
        const loaded = offset + aPageOfPlaces.length;
        const shown = filteredPlaces.length;
        (_b = placeStack.current) === null || _b === void 0 ? void 0 : _b.splice(level);
        placeStack.current.push({
            places: [...filteredPlaces],
            allPlaceIds,
            loading: false,
            loaded,
            max,
            shown,
            filtered: loaded - shown,
            parentPlaceId,
        });
    }, [updatePlaceLookup]);
    const client = useApolloClient();
    const fetchChildren = useCallback((parentId, level, offset = 0) => {
        var _a, _b;
        const keyword = (_a = filters.current.find(f => f.queryFieldKey === QueryFieldKey.KEYWORD)) === null || _a === void 0 ? void 0 : _a.id;
        const tag = (_b = filters.current
            .filter(f => f.queryFieldKey === QueryFieldKey.PLACE_TAG)) === null || _b === void 0 ? void 0 : _b.map(z => z.id);
        return client
            .query({
            query: rootLevelPlacesQuery,
            variables: {
                parentId,
                offset,
                keyword,
                tag,
                limit: 200,
            },
            fetchPolicy: 'no-cache',
        })
            .then(z => {
            var _a, _b, _c, _d;
            const items = (_b = (_a = z.data) === null || _a === void 0 ? void 0 : _a.rootLevelPlaces) === null || _b === void 0 ? void 0 : _b.items;
            if (items) {
                updatePlaceStack(items, (_d = (_c = z.data) === null || _c === void 0 ? void 0 : _c.rootLevelPlaces) === null || _d === void 0 ? void 0 : _d.totalCount, parentId, level, offset);
                forceUpdate();
                return items;
            }
            else {
                throw new Error('Cannot have no items returning from a fetchChildren call');
            }
        })
            .catch(e => {
            console.log(e);
        });
    }, [client, forceUpdate, updatePlaceStack]);
    const updateSelectedPlaces = useCallback((level, place) => {
        if (!place)
            return;
        while (selectedPlaces.current.length - 1 >= level)
            selectedPlaces.current.pop();
        selectedPlaces.current.push(place);
        placeToShowDetails.current = place;
        if (place.noOfChildren) {
            if (placeStack.current[level + 1])
                placeStack.current.pop();
            placeStack.current.push({
                ...defaultPlaceLevelInfo,
                loading: true,
                parentPlaceId: place.id,
            });
            void fetchChildren(place.id, level + 1);
        }
        else {
            placeStack.current.splice(level + 1);
        }
        forceUpdate();
    }, [fetchChildren, forceUpdate]);
    const handleLoadNextPage = useCallback((level) => {
        const parentId = level > 0 ? selectedPlaces.current[level - 1].id : null;
        placeStack.current[level].loading = true;
        void fetchChildren(parentId, level, placeStack.current[level].loaded);
        forceUpdate();
    }, [fetchChildren, forceUpdate]);
    const refreshSelectedPlace = useCallback(() => {
        const level = placeStack.current.length - 1;
        const parentId = level > 0 ? selectedPlaces.current[level - 1].id : null;
        placeStack.current[level].loading = true;
        void fetchChildren(parentId, level, 0);
        forceUpdate();
    }, [fetchChildren, forceUpdate]);
    const selectNextAvailablePlace = useCallback((level) => {
        var _a;
        if (placeStack.current[level].places.length) {
            const prevSelectedId = (_a = selectedPlaces.current[level]) === null || _a === void 0 ? void 0 : _a.id;
            const prevSelectedPlace = prevSelectedId
                ? placeStack.current[level].places.find(p => p.id === prevSelectedId)
                : null;
            updateSelectedPlaces(level, prevSelectedPlace !== null && prevSelectedPlace !== void 0 ? prevSelectedPlace : placeStack.current[level].places[0]);
        }
        else if (level <= 0) {
            selectedPlaces.current = [];
            placeToShowDetails.current = null;
        }
        else
            selectNextAvailablePlace(level - 1);
    }, [updateSelectedPlaces]);
    totalCount.current = (_b = (_a = placeCountData === null || placeCountData === void 0 ? void 0 : placeCountData.places) === null || _a === void 0 ? void 0 : _a.totalCount) !== null && _b !== void 0 ? _b : 0;
    const refetchRootPlaces = useCallback(() => {
        placeStack.current = [];
        placeStack.current.push({
            ...defaultPlaceLevelInfo,
            loading: true,
        });
        fetchChildren(null, 0)
            .then(() => {
            selectNextAvailablePlace(0);
        })
            .catch(e => {
            console.log(e);
        });
        forceUpdate();
    }, [fetchChildren, forceUpdate, selectNextAvailablePlace]);
    const refetchInitialData = useCallback(() => {
        void refetchRootPlaces();
        void refetchPlaceCount();
    }, [refetchPlaceCount, refetchRootPlaces]);
    useEffect(() => {
        refetchRootPlaces();
        // run only at startup
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const doReset = useCallback(() => {
        refetchInitialData();
        isDirty.current = false;
    }, [refetchInitialData]);
    const updatePlace = useCallback((place, isCascadingUpdate = false) => {
        return upsertPlace({ id: place.id, patch: patchFrom(place) })
            .then(() => {
            if (isCascadingUpdate) {
                refreshSelectedPlace();
            }
            else {
                forceUpdate();
            }
        })
            .catch(e => console.log(e));
    }, [forceUpdate, refreshSelectedPlace, upsertPlace]);
    const handleDeleteConfirmed = useCallback(() => {
        const p = placeToDelete.current;
        const level = selectedPlaces.current.length - 1;
        const newPlaces = placeStack.current[level].places.filter(z => z.id !== p.id);
        // using a difference, because we may not have deleted anything at all (rare)
        const difference = placeStack.current[level].places.length - newPlaces.length;
        if (newPlaces.length === 0 && level > 0) {
            placeStack.current.splice(level, 1);
        }
        else {
            placeStack.current[level] = {
                places: newPlaces,
                allPlaceIds: newPlaces.map(z => z.id),
                loading: false,
                parentPlaceId: placeStack.current[level].parentPlaceId,
                loaded: newPlaces.length,
                // max will also be reduces
                max: placeStack.current[level].max - difference,
                // we can only delete shown places
                shown: placeStack.current[level].shown - difference,
                // wont change because we cannot delete hidden places
                filtered: placeStack.current[level].filtered,
            };
        }
        if (level > 0)
            selectedPlaces.current[level - 1].noOfChildren =
                selectedPlaces.current[level - 1].noOfChildren - 1;
        isDirty.current = true;
        setConfirmingDelete(false);
        return deletePlace({ id: p.id }).then(() => {
            isDirty.current = false;
            setCheckTitle('');
            selectNextAvailablePlace(level);
            return refetchPlaceCount();
        });
    }, [deletePlace, refetchPlaceCount, selectNextAvailablePlace]);
    const handleDelete = useCallback(() => {
        placeToDelete.current = placeToShowDetails.current;
        setConfirmingDelete(true);
    }, []);
    const handleAdd = useCallback((title, level) => {
        var _a, _b, _c, _d, _e, _f;
        const parent = level === 0 ? null : selectedPlaces.current[level - 1];
        const place = new PlaceDisplayItem({
            saved: false,
            id: uuid.v4(),
            title: title,
            parentId: parent ? parent.id : undefined,
            tags: [],
            children: [],
            timezone: parent ? parent.timezone : DefaultTimezone,
            enabled: parent ? parent.enabled : true,
        });
        if (level > 0)
            // increment the parent place's no of children
            selectedPlaces.current[level - 1].noOfChildren =
                ((_a = selectedPlaces.current[level - 1].noOfChildren) !== null && _a !== void 0 ? _a : 0) + 1;
        if (placeStack.current[level]) {
            placeStack.current[level].max = ((_c = (_b = placeStack.current[level]) === null || _b === void 0 ? void 0 : _b.max) !== null && _c !== void 0 ? _c : 0) + 1;
            // placeStack.current[level].loaded = (placeStack.current[level].loaded ?? 0) + 1
            // placeStack.current[level].shown = (placeStack.current[level].shown ?? 0) + 1
        }
        updatePlaceStack([place], (_e = (_d = placeStack.current[level]) === null || _d === void 0 ? void 0 : _d.max) !== null && _e !== void 0 ? _e : 1, parent === null || parent === void 0 ? void 0 : parent.id, level, (_f = placeStack.current[level]) === null || _f === void 0 ? void 0 : _f.loaded);
        return updatePlace(place).then(() => {
            selectNextAvailablePlace(level);
        });
    }, [selectNextAvailablePlace, updatePlace, updatePlaceStack]);
    const handleDeleteCancelled = useCallback(() => setConfirmingDelete(false), []);
    const enablePlaceAndItsChildren = useCallback((enabled) => {
        placeToShowDetails.current.enabled = enabled;
        void updatePlace(placeToShowDetails.current, true);
    }, [updatePlace]);
    const handleChangeTimezone = useCallback(timezone => {
        var _a;
        const tz = (_a = timezone === null || timezone === void 0 ? void 0 : timezone[0]) === null || _a === void 0 ? void 0 : _a.id;
        if (!tz)
            return;
        placeToShowDetails.current.timezone = tz;
        void updatePlace(placeToShowDetails.current, true);
    }, [updatePlace]);
    const handleChangeTags = useCallback(tags => {
        placeToShowDetails.current.tags = tags;
        void updatePlace(placeToShowDetails.current);
    }, [updatePlace]);
    const handleAddTag = useCallback(newTag => {
        setNewTags(uniq([...newTags, newTag]));
        return newTag;
    }, [newTags]);
    const handleChangeTitle = useCallback((newTitle) => {
        placeToShowDetails.current.title = newTitle;
        void updatePlace(placeToShowDetails.current, true);
    }, [updatePlace]);
    const handleSelectPlace = useCallback((place, level) => {
        updateSelectedPlaces(level, place);
    }, [updateSelectedPlaces]);
    const handleAddFilter = useCallback((filter) => {
        const alreadyExists = findFilterByKey(filters.current, filter.queryFieldKey, filter.id);
        if (!alreadyExists) {
            filters.current = [...filters.current, filter];
            refetchRootPlaces();
        }
    }, [refetchRootPlaces]);
    const handleRemoveFilter = useCallback((filter) => {
        filters.current = filters.current.filter(fb => !(fb.id === filter.id && fb.queryFieldKey === filter.queryFieldKey));
        refetchRootPlaces();
    }, [refetchRootPlaces]);
    const handleToggleFilter = useCallback((filter) => {
        var _a;
        const isDate = ((_a = QueryFieldRegistry[filter.queryFieldKey]) === null || _a === void 0 ? void 0 : _a.fieldType) === BasicFieldType.DATE;
        const found = isDate
            ? filters.current.find(fb => fb.queryFieldKey === filter.queryFieldKey)
            : filters.current.find(fb => fb.queryFieldKey === filter.queryFieldKey && fb.id === filter.id);
        if (found)
            handleRemoveFilter(filter);
        if (!found || isDate)
            handleAddFilter(filter);
    }, [handleRemoveFilter, handleAddFilter]);
    const handleResetFilters = useCallback(() => {
        filters.current = [];
        refetchRootPlaces();
    }, [refetchRootPlaces]);
    const toolbar = useMemo(() => {
        acknowledgeUpdate(tick);
        return (React.createElement(PlaceFiltersBar, { filters: filters.current, onAddFilter: handleAddFilter, onToggleFilter: handleToggleFilter, onRemoveFilter: handleRemoveFilter }));
    }, [acknowledgeUpdate, tick, handleAddFilter, handleToggleFilter, handleRemoveFilter]);
    const renderDots = () => {
        return React.createElement(Dots, { color: Palette.primary, style: { margin: '40px auto' } });
    };
    const renderOpenDetails = useCallback(() => {
        var _a, _b;
        const selectedPlace = placeToShowDetails.current;
        return selectedPlace ? (React.createElement(Vertical, { shrink: true, gutter: true, padding: true },
            React.createElement(Text, { variant: "h1" }, selectedPlace.title),
            React.createElement(Horizontal, { centered: true, gutter: true },
                React.createElement(Text, { variant: "h3" }, i18n.t('title')),
                React.createElement(Pane, { classes: { root: myCss.editBox }, padding: true },
                    React.createElement(TextFieldWithStateAndBlur, { white: true, inputProps: {
                            style: {
                                fontSize: 16,
                                lineHeight: '20px',
                                color: Palette.secondary.main,
                            },
                        }, fullWidth: true, noMargin: true, noBorder: true, value: selectedPlace.title, onBlur: handleChangeTitle }))),
            React.createElement(Horizontal, { centered: true, gutter: true },
                React.createElement(Text, { variant: "h3" }, i18n.t('enabled')),
                React.createElement(Pane, { classes: { root: myCss.editBox }, padding: true },
                    React.createElement(Switch, { checked: selectedPlace.enabled, onChange: enablePlaceAndItsChildren, disabled: selectedPlace.parentId &&
                            !((_a = placeLookup.current[selectedPlace.parentId]) === null || _a === void 0 ? void 0 : _a.enabled) }))),
            React.createElement(Horizontal, { centered: true, gutter: true },
                React.createElement(Text, { variant: "h3" }, i18n.t('tags')),
                React.createElement(Pane, { classes: { root: myCss.editBox } },
                    React.createElement(ValueChooser, { noBorder: true, small: true, type: DataType.PLACE, fieldName: "tags", multi: true, value: selectedPlace.tags || [], label: i18n.t('select-tags'), tooltip: markdown(i18n.t('tags-description')), addLabel: i18n.t('create-tag'), extraValues: newTags, onChange: handleChangeTags, onAddNew: handleAddTag }))),
            React.createElement(Horizontal, { centered: true, gutter: true },
                React.createElement(Text, { variant: "h3" }, i18n.t('timezone')),
                React.createElement(Pane, { classes: { root: myCss.editBox } },
                    React.createElement(TimezoneChooser, { noBorder: true, required: true, value: [{ id: selectedPlace.timezone, title: selectedPlace.timezone }], onChange: handleChangeTimezone }))),
            React.createElement(Horizontal, { centered: true, gutter: true },
                React.createElement(Button, { color: Palette.warning, fullWidth: true, onClick: handleDelete, label: i18n.t('delete') })),
            React.createElement(ConfirmDialog, { open: confirmingDelete, color: Palette.warning, onConfirm: handleDeleteConfirmed, onClose: handleDeleteCancelled, canConfirm: checkTitle === selectedPlace.title },
                React.createElement(Pane, null,
                    React.createElement(Text, null, i18n.t('confirm-place-delete', {
                        count: (_b = selectedPlace.noOfChildren) !== null && _b !== void 0 ? _b : 0,
                    })),
                    React.createElement(TextField, { fullWidth: true, value: checkTitle, onChange: setCheckTitle, placeholder: selectedPlace.title }))))) : null;
    }, [
        myCss.editBox,
        handleChangeTitle,
        enablePlaceAndItsChildren,
        newTags,
        handleChangeTags,
        handleAddTag,
        handleChangeTimezone,
        handleDelete,
        confirmingDelete,
        handleDeleteConfirmed,
        handleDeleteCancelled,
        checkTitle,
    ]);
    const renderPlaceList = useCallback((levelInfo, level) => {
        var _a;
        const { places, loading, max, loaded, shown, filtered } = levelInfo;
        return (React.createElement("div", { className: myCss.placeList, key: `places_${level}` }, loading ? (React.createElement(Dots, { style: {
                margin: '32px auto',
            }, color: Palette.primary, key: `places_${level}` })) : (React.createElement(Vertical, { style: {
                borderStyle: 'solid',
                borderWidth: 0,
                borderRightWidth: 1,
                borderColor: Palette.BorderGrey.main,
            } },
            React.createElement(PlaceList, { places: places, level: level, selectedPlace: (_a = selectedPlaces.current[level]) === null || _a === void 0 ? void 0 : _a.id, onRowPressed: handleSelectPlace, total: max, shown: shown, loaded: loaded, filtered: filtered, resetFilters: handleResetFilters, loadNextPage: handleLoadNextPage, createNew: handleAdd })))));
    }, [handleAdd, handleLoadNextPage, handleResetFilters, handleSelectPlace, myCss.placeList]);
    return (React.createElement(SetupPlacesPageShell, { loading: false },
        React.createElement(Vertical, { hidden: true, gutter: true, style: { padding: '8px 0 8px 16px' } },
            React.createElement(Horizontal, { shrink: true, centered: true, gutter: true },
                toolbar,
                React.createElement(ActionButtonContainer, { shrink: true, justify: "flex-end" },
                    React.createElement(ActionButton, { Icon: RepeatIcon, tooltip: isDirty.current
                            ? i18n.t('cancel-changes-and-reload')
                            : i18n.t('reload-desc'), label: isDirty.current ? i18n.t('cancel') : i18n.t('reload'), onClick: doReset }))),
            React.createElement(Pane, { style: { minHeight: 16 } }, isDirty.current || loadingPlaceCount ? (React.createElement(Dots, { color: Palette.primary })) : (React.createElement(Text, { variant: "h3" }, `${i18n.t('total-places')}: ${totalCount.current}`))),
            React.createElement(Horizontal, { justify: 'flex-start', scroll: true, padding: true }, (_c = placeStack.current) === null || _c === void 0 ? void 0 :
                _c.map((z, i) => renderPlaceList(z, i)),
                !selectedPlaces.current[placeStack.current.length - 1] ||
                    !placeStack.current ||
                    ((_d = placeStack.current[placeStack.current.length - 1]) === null || _d === void 0 ? void 0 : _d.loading)
                    ? null
                    : renderPlaceList(defaultPlaceLevelInfo, placeStack.current.length))),
        placeToShowDetails.current ? (React.createElement(SidePaneStack, { sidePanes: [
                {
                    label: i18n.t('details'),
                    width: 380,
                    startOpen: true,
                    renderOpening: renderDots,
                    renderOpen: renderOpenDetails,
                },
            ] })) : null));
}
export default SetupPlacesPage;
function SetupPlacesPageShell({ loading, children }) {
    return (React.createElement(AppPage, null,
        React.createElement(Pane, { grow: true },
            React.createElement(Spinner, { busy: loading },
                React.createElement(Horizontal, { stretch: true, children: children })))));
}
function patchFrom({ title, seqNo, parentId, tags, timezone, enabled }) {
    return { title, seqNo, parentId, tags, timezone, enabled };
}
